import { ICoordinate, IExpeditionType, ILocation, IServiceInfo } from '../models'
import { flatten, getRegion } from './maps'

type Ring = number[][]

// GeoJSON rings are [lng, lat]. Ray casting wraps the last vertex back to the
// first, so unclosed rings work without normalising them.
const isPointInRing = (longitude: number, latitude: number, ring: Ring): boolean => {
  let inside = false

  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i]
    const [xj, yj] = ring[j]
    const crossesRay = (yi > latitude) !== (yj > latitude) &&
      longitude < ((xj - xi) * (latitude - yi)) / (yj - yi) + xi

    if (crossesRay) inside = !inside
  }

  return inside
}

// A polygon's first ring is its outline, the rest are holes.
const isPointInPolygon = (longitude: number, latitude: number, rings: Ring[]): boolean => {
  const [outline, ...holes] = rings
  if (!Array.isArray(outline)) return false
  if (!isPointInRing(longitude, latitude, outline)) return false

  return !holes.some(hole => Array.isArray(hole) && isPointInRing(longitude, latitude, hole))
}

const isPointInOutline = (longitude: number, latitude: number, outline: ILocation): boolean => {
  const geometry: any = outline?.geometry
  if (!geometry?.coordinates) return false

  if (geometry.type === 'Polygon') {
    return isPointInPolygon(longitude, latitude, geometry.coordinates as Ring[])
  }

  if (geometry.type === 'MultiPolygon') {
    return (geometry.coordinates as Ring[][]).some(rings => isPointInPolygon(longitude, latitude, rings))
  }

  return false
}

// Same region the map frames itself to, so recentring lands where the service is.
export const getServiceAreaCenter = (serviceInfos: IServiceInfo[]): ICoordinate | null => {
  if (!serviceInfos?.length) return null

  const noneSelected = serviceInfos.every(serviceInfo => !serviceInfo.selected)
  const region = getRegion({
    features: serviceInfos
      .filter(serviceInfo => noneSelected || serviceInfo.selected)
      .map(serviceInfo => serviceInfo.stops.map(stop => stop.location))
      .reduce(flatten, [])
  })

  if (!region) return null
  return { latitude: region.latitude, longitude: region.longitude }
}

export const isDoor2Door = (serviceInfo: IServiceInfo): boolean =>
  serviceInfo.expeditionType === IExpeditionType.Door2Door ||
  serviceInfo.expeditionType === IExpeditionType.HybridDoor2Door ||
  serviceInfo.expeditionType === IExpeditionType.DynamicDoor2Door

const hasOutlines = (service: IServiceInfo): boolean =>
  Array.isArray(service.outlines) && !service.outlines.every(outline => !outline)

export const anyServiceHasOutlines = (serviceInfos: IServiceInfo[]): boolean => serviceInfos.some(hasOutlines)

export const isPointInsideOutlines = (serviceInfos: IServiceInfo[], coords: ICoordinate): boolean => {
  const { latitude, longitude } = coords

  return serviceInfos
    .filter(hasOutlines)
    .some(service => service.outlines.some(outline => isPointInOutline(longitude, latitude, outline)))
}
